import React, { useState } from "react";
import { Upload, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const CATEGORIES = ["IT", "HR", "Payroll", "Facilities", "Security", "Other"];
const PRIORITIES  = ["P1 - Critical", "P2 - High", "P3 - Medium", "P4 - Low"];

export default function TicketSubmissionForm({ onSubmitSuccess }) {
  const [form,       setForm]       = useState({ subject: "", description: "", category: "", priority: "P3 - Medium" });
  const [attachment, setAttachment] = useState(null);
  const [busy,       setBusy]       = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) { setError("Subject is required."); return; }
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (attachment) fd.append("attachment", attachment);

      const res = await fetch(`${API_BASE}/api/tickets/submit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubmitted(true);
      setForm({ subject: "", description: "", category: "", priority: "P3 - Medium" });
      setAttachment(null);
      setTimeout(() => setSubmitted(false), 3000);
      onSubmitSuccess?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Submit a Ticket</span>
      </div>

      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div>
          <label className={labelCls}>Subject</label>
          <input value={form.subject} onChange={e => set("subject", e.target.value)}
            placeholder="Brief description of the issue"
            className={inputCls} required />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Category</label>
            <select value={form.category} onChange={e => set("category", e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Priority</label>
            <select value={form.priority} onChange={e => set("priority", e.target.value)} className={inputCls}>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Description</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)}
            placeholder="Describe the issue in detail…"
            rows={3} className={`${inputCls} resize-none placeholder-white/20`} required />
        </div>

        <div>
          <label className={labelCls}>Attachment (optional)</label>
          <div className="border border-dashed border-[#00ff90]/20 rounded-lg px-4 py-3 bg-[#00ff90]/5 flex items-center gap-3">
            <Upload size={15} className="text-[#00ff90]/40 shrink-0" />
            <input type="file" onChange={e => setAttachment(e.target.files[0])}
              className="text-sm text-white/50 w-full" />
            {attachment && (
              <button type="button" onClick={() => setAttachment(null)} className="text-white/30 hover:text-white shrink-0">
                <X size={13} />
              </button>
            )}
          </div>
          {attachment && <p className="text-xs text-white/30 mt-1 truncate">📎 {attachment.name}</p>}
        </div>

        {error     && <p className="text-red-400 text-xs">⚠ {error}</p>}
        {submitted && <p className="text-[#00ff90] text-xs">✓ Ticket submitted successfully.</p>}

        <div className="flex justify-end">
          <button type="submit" disabled={busy}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
            {busy ? "Submitting…" : "Submit Ticket"}
          </button>
        </div>
      </form>
    </div>
  );
}